import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Columns3, LayoutGrid, List, Mic, Plus, Rocket, Search } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeParams } from "@/components/DateRangeFilter";
import { Pill } from "@/components/Music";
import { TrackDrawer } from "@/components/Tracks";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const STAGES = [
  { key: "Voice", field: "voice", done: "Yes", hint: "Yes · In process · No" },
  { key: "Harmony", field: "harmony", done: "Yes", hint: "Yes · In process · Pending · No" },
  { key: "Mix", field: "mix", done: "Finalized", hint: "Pending · In process · Completed · Finalized" },
  { key: "Mastered", field: "mastered", done: "Completed", hint: "Pending · In process · Completed" },
  { key: "Ready", field: null, done: null, hint: "Needs a date, then it's released" },
];
const ORDER = ["Voice", "Harmony", "Mix", "Mastered", "Ready", "Released"];
const DATE_FIELDS = [
  { value: "created_at", label: "Date added" },
  { value: "release_date", label: "Release date" },
];
const TONE = { "In process": "brand", Pending: "warn", Completed: "good", Finalized: "good", Yes: "good", No: "bad" };

function Progress({ stage }) {
  const idx = ORDER.indexOf(stage);
  return (
    <span className="flex gap-[3px]" aria-label={`Step ${Math.min(idx + 1, 5)} of 5`}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={cn("w-3.5 h-1 rounded-full", i < idx ? "bg-brand" : "bg-line")} />
      ))}
    </span>
  );
}

function ReleaseButton({ t, onDone }) {
  const [date, setDate] = useState(t.release_date || new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand">
          <Rocket className="w-3.5 h-3.5" /> Release
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 space-y-3">
        <div className="text-[12px] font-semibold">Release date</div>
        <Calendar mode="single" value={date} onPick={setDate} />
        <Button
          size="sm"
          className="w-full bg-brand hover:bg-brand-hover"
          onClick={() => {
            setOpen(false);
            onDone({ released: "Yes", release_date: date });
          }}
        >
          Mark released
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export default function Production() {
  const { settings } = useSettings();
  const [mode, setMode] = useViewMode("production", "board");
  const [range, setRange] = useState({ ...EMPTY_RANGE, field: "created_at" });
  const [q, setQ] = useState("");
  const [showReleased, setShowReleased] = useState(false);
  const [rows, setRows] = useRemembered("production");
  const [open, setOpen] = useState(null);
  const req = useRef(0);

  const load = useCallback(async () => {
    const id = ++req.current;
    try {
      const { data } = await api.get("/tracks", { params: { q, filter: showReleased ? "all" : "unreleased", sort: "updated", ...rangeParams(range) } });
      if (id === req.current) setRows(data.tracks, !q && !showReleased && !range.from && !range.to);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [q, range, showReleased, setRows]);

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const patch = async (t, body) => {
    try {
      const { data } = await api.patch(`/tracks/${t.track_id}/production`, body);
      setRows((rs) => rs.map((r) => (r.track_id === t.track_id ? data : r)));
      if (data.stage !== t.stage) toast.success(`${t.title} → ${data.stage}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const advance = (t) => {
    const s = STAGES.find((x) => x.key === t.stage);
    if (s?.field) patch(t, { [s.field]: s.done });
  };

  const byStage = (key) => (rows || []).filter((t) => t.stage === key);
  const po = settings.production_options;
  const current = open ? (rows || []).find((t) => t.track_id === open) : null;
  const inStudio = (rows || []).filter((t) => t.stage !== "Released").length;

  const card = (t) => {
    const s = STAGES.find((x) => x.key === t.stage);
    const value = s?.field ? t[s.field] : null;
    return (
      <div key={t.track_id} className="panel p-3.5 space-y-2.5">
        <button onClick={() => setOpen(t.track_id)} className="text-left block w-full">
          <div className="text-[13.5px] font-semibold truncate">{t.title}</div>
          <div className="text-[12px] text-muted2 truncate">{t.artist || "—"}{t.riddim ? ` · ${t.riddim}` : ""}</div>
        </button>
        <div className="flex items-center justify-between gap-2">
          {t.stage === "Released" ? (
            <Pill tone="good">Released {t.release_date ? formatDate(t.release_date) : ""}</Pill>
          ) : t.stage === "Ready" ? (
            <Pill tone="warn">{t.release_date ? `Out ${formatDate(t.release_date)}` : "No date yet"}</Pill>
          ) : (
            <Pill tone={TONE[value] || "muted"}>{value || "Not started"}</Pill>
          )}
          <Progress stage={t.stage} />
        </div>
        {t.stage !== "Released" && (
          <div className="flex items-center justify-between pt-2 border-t border-line">
            <Link to={`/catalog/${t.track_id}`} className="text-[12px] text-muted2 hover:text-ink">
              Edit
            </Link>
            {t.stage === "Ready" ? (
              <ReleaseButton t={t} onDone={(b) => patch(t, b)} />
            ) : (
              <button onClick={() => advance(t)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand">
                {s.field === "mix" ? "Finalize" : s.field === "mastered" ? "Mastered" : "Done"} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout
      title="Production"
      subtitle={rows ? `${inStudio} track${inStudio === 1 ? "" : "s"} in the studio` : "Loading…"}
      action={
        <Button asChild className="bg-brand hover:bg-brand-hover">
          <Link to="/catalog/new">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New track</span>
          </Link>
        </Button>
      }
    >
      <StickyFilters className="-mt-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" aria-label="Search" className="h-9 w-[220px] pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
          </label>
          <DateRangeFilter value={range} onChange={setRange} fields={DATE_FIELDS} />
          <label className="inline-flex items-center gap-2 text-[13px] text-muted2 ml-1 cursor-pointer">
            <input type="checkbox" checked={showReleased} onChange={(e) => setShowReleased(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--brand))]" />
            Include released
          </label>
          <div className="flex-1" />
          <ViewToggle
            mode={mode}
            onChange={setMode}
            options={[
              { value: "board", label: "Board", icon: Columns3 },
              { value: "list", label: "List", icon: List },
              { value: "grid", label: "Cards", icon: LayoutGrid },
            ]}
          />
        </div>
      </StickyFilters>

      {!rows ? (
        <LoadingPanel rows={6} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState icon={Mic} title="Nothing in the studio" body="Tracks that aren't released yet show up here, moving from Voice through to Ready." />
        </div>
      ) : mode === "board" ? (
        <div className="overflow-x-auto pb-2">
          <div className="grid grid-cols-[repeat(5,minmax(230px,1fr))] gap-3 items-start min-w-[1180px]">
            {STAGES.map((s) => (
              <section key={s.key} className="rounded-2xl bg-line/40 p-3 space-y-2.5">
                <div className="flex items-center justify-between px-1 pt-1">
                  <h2 className="font-display text-[16px] font-semibold">{s.key}</h2>
                  <span className="font-mono text-[11px] text-muted2">{byStage(s.key).length}</span>
                </div>
                <p className="text-[11.5px] text-muted2 px-1">{s.hint}</p>
                {byStage(s.key).map(card)}
                {!byStage(s.key).length && <p className="text-[12px] text-muted2 px-1 py-4 text-center">Empty</p>}
              </section>
            ))}
          </div>
          {showReleased && byStage("Released").length > 0 && <p className="text-[12.5px] text-muted2 mt-3">{byStage("Released").length} released tracks are hidden on the board — switch to List or Cards to see them.</p>}
        </div>
      ) : mode === "grid" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">{rows.map(card)}</div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["Track", "Voice", "Harmony", "Mix", "Mastered", "Stage", "Release date", ""].map((h) => (
                    <th key={h} className="t-head text-left px-3 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.track_id} className="t-row">
                    <td className="t-cell max-w-[240px]">
                      <button onClick={() => setOpen(t.track_id)} className="text-left block max-w-full">
                        <div className="font-semibold truncate hover:text-brand">{t.title}</div>
                        <div className="text-[12px] text-muted2 truncate">{t.artist}</div>
                      </button>
                    </td>
                    {["voice", "harmony", "mix", "mastered"].map((f) => (
                      <td key={f} className="t-cell">
                        <select
                          aria-label={`${f} for ${t.title}`}
                          value={t[f] || ""}
                          onChange={(e) => patch(t, { [f]: e.target.value })}
                          className="h-8 rounded-lg border border-line bg-card px-2 text-[12.5px] max-w-[130px]"
                        >
                          <option value="">—</option>
                          {po[f].map((o) => (
                            <option key={o}>{o}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                    <td className="t-cell">
                      <div className="flex items-center gap-2">
                        <Pill tone={t.stage === "Released" ? "good" : t.stage === "Ready" ? "warn" : "brand"}>{t.stage}</Pill>
                      </div>
                    </td>
                    <td className="t-cell text-[12.5px] whitespace-nowrap">{t.release_date ? formatDate(t.release_date) : <span className="text-muted2">—</span>}</td>
                    <td className="t-cell text-right">{t.stage === "Ready" && <ReleaseButton t={t} onDone={(b) => patch(t, b)} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <TrackDrawer track={current} open={!!current} onOpenChange={(v) => !v && setOpen(null)} canEdit />
    </Layout>
  );
}
